import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { homeTranslations } from '../translations/home';

/**
 * Footer Component
 * 
 * Company footer with contact information and locations.
 * Reusable across all pages.
 * 
 * Sections:
 * - Company Info: Logo, description, sales email
 * - Quick Links: Navigation to main service pages
 * - Our Locations: 5 office locations with addresses and phone numbers
 * - Bottom Bar: Copyright, privacy policy, terms of service
 * 
 * Features:
 * - id="contact" for navigation from header
 * - Bilingual support (EN/ES)
 * - Clickable email and phone links
 */
const Footer = () => {
  const { language } = useLanguage();
  const t = homeTranslations[language].footer;

  const locations = [
    {
      city: 'Durango, Mexico',
      address: 'Circuito Los Cipreses #303, Villa Blanca',
      phone: '+52 618 171 9959'
    },
    {
      city: 'Monterrey',
      phone: '+52 81 2404 6240'
    },
    {
      city: 'San Luis Potosí',
      phone: '+52 444 578 4826'
    },
    {
      city: 'Querétaro',
      phone: '+52 442 469 3142'
    }
  ];

  const quickLinks = [
    { name: t.links[0], href: '/cnc' },
    { name: t.links[1], href: '/harness' },
    { name: t.links[2], href: '/automation' },
    { name: t.links[3], href: '#contact' }
  ];

  return (
    <footer id="contact" className="bg-black border-t border-gray-900">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="text-2xl font-bold mb-4">
              <span className="text-[#B91C1C]">DSI</span>
              <span className="text-white text-sm ml-2">DURANGO</span>
            </div>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              {t.description}
            </p>
            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-gray-400 hover:text-[#B91C1C] ">
                <Mail size={18} />
                <a href={`mailto:${t.emailAddress}`} className="text-sm">
                  {t.emailAddress}
                </a>
              </div>
              <a 
                href="https://wa.me/526181719959?text=Hola,%20me%20interesa%20información%20sobre%20sus%20servicios%20industriales"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-[#25D366] hover:text-[#20BA5A] "
              >
                <MessageCircle size={18} />
                <span className="text-sm font-medium">WhatsApp: +52 618 171 9959</span>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">{t.quickLinks}</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    className="text-gray-400 hover:text-[#B91C1C] text-sm  hover:translate-x-1 inline-block"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations - Split into 2 columns on larger screens */}
          <div className="lg:col-span-2">
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">{t.locations}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {locations.map((location, index) => (
                <div key={index} className="bg-gray-900/50 rounded-lg p-4 border border-gray-800 ">
                  <h4 className="text-white font-semibold text-sm mb-2">{location.city}</h4>
                  {location.address ? (<div className="flex items-start space-x-2 mb-2">
                    <MapPin size={14} className="text-[#B91C1C] mt-1 flex-shrink-0" />
                    <p className="text-gray-400 text-xs leading-relaxed">{location.address}</p>
                  </div>) : <div></div>}
                  
                  <div className="flex items-center space-x-2">
                    <Phone size={14} className="text-[#B91C1C] flex-shrink-0" />
                    <a href={`tel:${location.phone}`} className="text-gray-400 hover:text-[#B91C1C] text-xs ">
                      {location.phone}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {t.copyright}
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <Link to="/privacy-policy" className="text-gray-500 hover:text-[#B91C1C] transition-colors">{t.privacy}</Link>
              <Link to="/terms-of-service" className="text-gray-500 hover:text-[#B91C1C] transition-colors">{t.terms}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
